import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Input,
  TextArea,
  Icon,
  Header,
  Segment,
  Message,
  Label,
  Container,
  Card,
  Divider,
  Form,
  Placeholder,
} from "semantic-ui-react";
import { collegeProgramApi } from "@api/colleges/colleges";

const CollegeProgramManagement = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // College Modal States
  const [collegeModal, setCollegeModal] = useState({
    open: false,
    mode: "create",
    data: {},
  });
  const [collegeForm, setCollegeForm] = useState({ name: "", description: "" });
  const [collegeSaving, setCollegeSaving] = useState(false);

  // Program Modal States
  const [programModal, setProgramModal] = useState({
    open: false,
    mode: "create",
    data: {},
    collegeId: null,
  });
  const [programForm, setProgramForm] = useState({
    name: "",
    code: "",
  });
  const [programSaving, setProgramSaving] = useState(false);
  const [selectedCollegePrograms, setSelectedCollegePrograms] = useState([]);

  // Delete Confirmation States
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: "",
    id: null,
    name: "",
  });
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Load colleges with programs
  const loadColleges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await collegeProgramApi.listCollegesWithPrograms();
      setColleges(response.data.data || []);
    } catch (err) {
      setError("Failed to load colleges and programs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadColleges();
  }, [loadColleges]);

  // Show success message with auto-hide
  const showSuccess = useCallback((message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 4000);
  }, []);

  // College CRUD Operations
  const handleCreateCollege = useCallback(() => {
    setCollegeForm({ name: "", description: "" });
    setCollegeModal({ open: true, mode: "create", data: {} });
  }, []);

  const handleEditCollege = useCallback((college) => {
    setCollegeForm({ name: college.name, description: college.description });
    setCollegeModal({ open: true, mode: "edit", data: college });
  }, []);

  const handleSaveCollege = async () => {
    try {
      setCollegeSaving(true);
      setError(null);

      if (collegeModal.mode === "create") {
        await collegeProgramApi.createCollege(collegeForm);
        showSuccess("College created successfully!");
      } else {
        await collegeProgramApi.updateCollege(
          collegeModal.data.id,
          collegeForm
        );
        showSuccess("College updated successfully!");
      }

      setCollegeModal({ open: false, mode: "create", data: {} });
      await loadColleges();
    } catch (err) {
      setError(
        `Failed to ${collegeModal.mode} college: ${
          err.response?.data?.message || err.message
        }`
      );
      console.error(`${collegeModal.mode} college error:`, err);
    } finally {
      setCollegeSaving(false);
    }
  };

  const handleDeleteCollege = useCallback((college) => {
    setDeleteConfirm({
      open: true,
      type: "college",
      id: college.id,
      name: college.name,
    });
  }, []);

  // Program CRUD Operations
  const handleManagePrograms = async (college) => {
    try {
      setSelectedCollegePrograms(college.programs || []);
      setProgramModal({
        open: true,
        mode: "manage",
        data: {},
        collegeId: college.id,
      });
    } catch (err) {
      setError("Failed to load programs");
    }
  };

  const handleCreateProgram = useCallback(() => {
    setProgramForm({ name: "", code: "" });
    setProgramModal((prev) => ({ ...prev, mode: "create", data: {} }));
  }, []);

  const handleEditProgram = useCallback((program) => {
    setProgramForm({
      name: program.name,
      code: program.code,
    });
    setProgramModal((prev) => ({ ...prev, mode: "edit", data: program }));
  }, []);

  const handleSaveProgram = async () => {
    try {
      setProgramSaving(true);
      setError(null);

      const programData = {
        name: programForm.name.trim(),
        code: programForm.code.trim().toUpperCase(),
        college_id: programModal.collegeId,
      };

      if (programModal.mode === "create") {
        await collegeProgramApi.createProgram(programData);
        showSuccess("Program created successfully!");
      } else {
        await collegeProgramApi.updateProgram(
          programModal.data.id,
          programData
        );
        showSuccess("Program updated successfully!");
      }

      // Refresh college data
      await loadColleges();

      // Update selected college programs with the latest data
      const updatedCollege = colleges.find(
        (c) => c.id === programModal.collegeId
      );
      if (updatedCollege) {
        try {
          const collegeResponse = await collegeProgramApi.getCollege(
            programModal.collegeId,
            true
          );
          setSelectedCollegePrograms(collegeResponse.data.data.programs || []);
        } catch (fetchError) {
          console.warn("Failed to fetch updated college data:", fetchError);
          // Fallback: refresh from the main colleges list
          const refreshedColleges =
            await collegeProgramApi.listCollegesWithPrograms();
          const refreshedCollege = refreshedColleges.data.data.find(
            (c) => c.id === programModal.collegeId
          );
          setSelectedCollegePrograms(refreshedCollege?.programs || []);
        }
      }

      // Reset to manage mode
      setProgramModal((prev) => ({ ...prev, mode: "manage", data: {} }));
      setProgramForm({ name: "", code: "" });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Unknown error";

      setError(
        `Failed to ${programModal.mode} program: ${JSON.stringify(
          errorMessage
        )}`
      );
      console.error(`${programModal.mode} program error:`, err);
    } finally {
      setProgramSaving(false);
    }
  };

  const handleDeleteProgram = useCallback((program) => {
    setDeleteConfirm({
      open: true,
      type: "program",
      id: program.id,
      name: program.name,
    });
  }, []);

  // Delete Confirmation Handler
  const handleConfirmDelete = async () => {
    try {
      setError(null);

      if (deleteConfirm.type === "college") {
        await collegeProgramApi.deleteCollege(deleteConfirm.id);
        showSuccess("College deleted successfully!");
        await loadColleges();
      } else {
        await collegeProgramApi.deleteProgram(deleteConfirm.id);
        showSuccess("Program deleted successfully!");

        // Refresh programs in modal
        const updatedPrograms = selectedCollegePrograms.filter(
          (p) => p.id !== deleteConfirm.id
        );
        setSelectedCollegePrograms(updatedPrograms);
        await loadColleges();
      }
    } catch (err) {
      setError(
        `Failed to delete ${deleteConfirm.type}: ${
          err.response?.data?.message || err.message
        }`
      );
      console.error(`Delete ${deleteConfirm.type} error:`, err);
    } finally {
      setDeleteConfirm({ open: false, type: "", id: null, name: "" });
      setDeleteConfirmText("");
    }
  };

  // Helper function to validate program form
  const isProgramFormValid = () => {
    return (
      programForm.name.trim().length > 0 &&
      programForm.code.trim().length > 0 &&
      programModal.collegeId
    );
  };

  // Close modals
  const closeCollegeModal = useCallback(() => {
    setCollegeModal({ open: false, mode: "create", data: {} });
    setCollegeForm({ name: "", description: "" });
  }, []);

  const closeProgramModal = useCallback(() => {
    setProgramModal({
      open: false,
      mode: "create",
      data: {},
      collegeId: null,
    });
    setProgramForm({ name: "", code: "" });
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirm({ open: false, type: "", id: null, name: "" });
    setDeleteConfirmText("");
  }, []);

  // Render loading placeholder
  const renderLoadingPlaceholder = () => (
    <Segment>
      <Placeholder>
        <Placeholder.Header>
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Header>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
      </Placeholder>
    </Segment>
  );

  return (
    <Container
      fluid
      style={{ padding: "2rem", maxWidth: "1400px", margin: "0 auto" }}
    >
      {/* Header Section */}
      <Card
        fluid
        style={{ marginBottom: "2rem", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
      >
        <Card.Content>
          <Header as="h1" style={{ margin: 0, color: "#225745" }}>
            <Icon name="university" size="huge" />
            <Header.Content>College & Program Management</Header.Content>
          </Header>
        </Card.Content>
      </Card>

      {/* Alert Messages */}
      {error && (
        <Message
          negative
          style={{ marginBottom: "1.5rem" }}
          onDismiss={() => setError(null)}
        >
          <Message.Header>
            <Icon name="exclamation triangle" />
            Error
          </Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message
          positive
          style={{ marginBottom: "1.5rem" }}
          onDismiss={() => setSuccess(null)}
        >
          <Message.Header>
            <Icon name="check circle" />
            Success
          </Message.Header>
          <p>{success}</p>
        </Message>
      )}

      {/* Action Bar */}
      <Card fluid style={{ marginBottom: "1.5rem" }}>
        <Card.Content>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Header as="h3" style={{ margin: 0 }}>
                Colleges ({colleges.length})
              </Header>
              <p style={{ margin: "0.5rem 0 0 0", color: "#666" }}>
                Manage your educational institutions and their programs
              </p>
            </div>
            <Button
              primary
              icon="plus"
              content="Add College"
              onClick={handleCreateCollege}
              size="medium"
              style={{ minWidth: "160px" }}
            />
          </div>
        </Card.Content>
      </Card>

      {/* Colleges Table */}
      {loading ? (
        renderLoadingPlaceholder()
      ) : (
        <Card fluid>
          <Card.Content style={{ padding: 0 }}>
            <Table celled striped stackable style={{ margin: 0 }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={{ fontSize: "1em", fontWeight: "bold" }}
                  >
                    <Icon name="university" />
                    Department Name
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{ fontSize: "1em", fontWeight: "bold" }}
                  >
                    <Icon name="info circle" />
                    Description
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      fontSize: "1em",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    <Icon name="graduation cap" />
                    Programs
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    style={{
                      fontSize: "1em",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    <Icon name="settings" />
                    Actions
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {colleges.map((college) => (
                  <Table.Row key={college.id}>
                    <Table.Cell
                      style={{ fontWeight: "600", fontSize: "1.1em" }}
                    >
                      {college.name}
                    </Table.Cell>
                    <Table.Cell style={{ maxWidth: "300px" }}>
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {college.description || (
                          <em style={{ color: "#999" }}>No description</em>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Label
                        color={college.programs?.length > 0 ? "blue" : "grey"}
                        size="medium"
                        style={{ minWidth: "80px" }}
                      >
                        <Icon name="graduation cap" />
                        {college.programs ? college.programs.length : 0}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Button.Group size="small">
                        <Button
                          icon="edit"
                          content="Edit"
                          onClick={() => handleEditCollege(college)}
                          style={{ borderRadius: "4px 0 0 4px" }}
                        />
                        <Button
                          icon="graduation cap"
                          content="Programs"
                          color="blue"
                          onClick={() => handleManagePrograms(college)}
                        />
                        <Button
                          icon="trash"
                          content="Delete"
                          color="red"
                          onClick={() => handleDeleteCollege(college)}
                          style={{ borderRadius: "0 4px 4px 0" }}
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {colleges.length === 0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan="4"
                      textAlign="center"
                      style={{ padding: "3rem" }}
                    >
                      <div style={{ color: "#999" }}>
                        <Icon
                          name="university"
                          size="huge"
                          style={{ opacity: 0.3 }}
                        />
                        <Header
                          as="h3"
                          style={{ color: "#999", marginTop: "1rem" }}
                        >
                          No colleges found
                        </Header>
                        <p>Get started by adding your first college!</p>
                        <Button
                          primary
                          icon="plus"
                          content="Add College"
                          onClick={handleCreateCollege}
                          style={{ marginTop: "1rem" }}
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Card.Content>
        </Card>
      )}

      {/* College Create/Edit Modal */}
      <Modal
        open={collegeModal.open}
        onClose={closeCollegeModal}
        size="small"
        closeIcon
      >
        <Modal.Header style={{ backgroundColor: "#f8f9fa", padding: "1.5rem" }}>
          <Icon name={collegeModal.mode === "create" ? "plus" : "edit"} />
          {collegeModal.mode === "create"
            ? "Create New College"
            : "Edit College"}
        </Modal.Header>
        <Modal.Content style={{ padding: "2rem" }}>
          <Form>
            <Form.Field required>
              <label
                style={{
                  fontSize: "1.1em",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                College Name
              </label>
              <Input
                fluid
                placeholder="Enter college name (e.g., College of Engineering)"
                value={collegeForm.name}
                onChange={(e, { value }) =>
                  setCollegeForm((prev) => ({ ...prev, name: value }))
                }
                error={collegeForm.name.trim() === ""}
              />
            </Form.Field>
            <Form.Field style={{ marginTop: "1.5rem" }}>
              <label
                style={{
                  fontSize: "1.1em",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Description
              </label>
              <TextArea
                placeholder="Enter college description (optional)"
                value={collegeForm.description}
                onChange={(e, { value }) =>
                  setCollegeForm((prev) => ({
                    ...prev,
                    description: value,
                  }))
                }
                rows={4}
                style={{ minHeight: "100px" }}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions
          style={{ padding: "1.5rem", backgroundColor: "#f8f9fa" }}
        >
          <Button onClick={closeCollegeModal} size="medium">
            <Icon name="cancel" />
            Cancel
          </Button>
          <Button
            primary
            loading={collegeSaving}
            disabled={!collegeForm.name.trim()}
            onClick={handleSaveCollege}
            size="medium"
          >
            <Icon name={collegeModal.mode === "create" ? "plus" : "save"} />
            {collegeModal.mode === "create"
              ? "Create College"
              : "Update College"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Program Management Modal */}
      <Modal
        open={programModal.open}
        onClose={closeProgramModal}
        size="large"
        closeIcon
      >
        <Modal.Header style={{ backgroundColor: "#f8f9fa", padding: "1.5rem" }}>
          <Icon name="graduation cap" />
          {programModal.mode === "manage"
            ? `Programs - ${
                colleges.find((c) => c.id === programModal.collegeId)?.name ||
                ""
              }`
            : programModal.mode === "create"
            ? "Create New Program"
            : "Edit Program"}
        </Modal.Header>
        <Modal.Content style={{ padding: "2rem" }}>
          {programModal.mode === "manage" && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                  padding: "1rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <Header as="h4" style={{ margin: 0 }}>
                    Programs ({selectedCollegePrograms.length})
                  </Header>
                  <p style={{ margin: "0.5rem 0 0 0", color: "#666" }}>
                    Manage academic programs for this college
                  </p>
                </div>
                <Button
                  primary
                  icon="plus"
                  content="Add Program"
                  onClick={handleCreateProgram}
                  size="medium"
                />
              </div>

              <Table celled stackable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell
                      style={{ fontSize: "1em", fontWeight: "bold" }}
                    >
                      <Icon name="code" />
                      Code
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{ fontSize: "1em", fontWeight: "bold" }}
                    >
                      <Icon name="book" />
                      Program Name
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      style={{
                        fontSize: "1em",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      <Icon name="settings" />
                      Actions
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {selectedCollegePrograms.map((program) => (
                    <Table.Row key={program.id}>
                      <Table.Cell>
                        <Label
                          color="teal"
                          size="medium"
                          style={{ fontWeight: "bold" }}
                        >
                          {program.code}
                        </Label>
                      </Table.Cell>
                      <Table.Cell
                        style={{ fontWeight: "600", fontSize: "1.1em" }}
                      >
                        {program.name}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Button.Group size="small">
                          <Button
                            icon="edit"
                            content="Edit"
                            onClick={() => handleEditProgram(program)}
                          />
                          <Button
                            icon="trash"
                            content="Delete"
                            color="red"
                            onClick={() => handleDeleteProgram(program)}
                          />
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                  {selectedCollegePrograms.length === 0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan="3"
                        textAlign="center"
                        style={{ padding: "3rem" }}
                      >
                        <div style={{ color: "#999" }}>
                          <Icon
                            name="graduation cap"
                            size="huge"
                            style={{ opacity: 0.3 }}
                          />
                          <Header
                            as="h4"
                            style={{ color: "#999", marginTop: "1rem" }}
                          >
                            No programs found
                          </Header>
                          <p>Add the first program for this college!</p>
                          <Button
                            primary
                            icon="plus"
                            content="Add Program"
                            onClick={handleCreateProgram}
                            style={{ marginTop: "1rem" }}
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </>
          )}

          {(programModal.mode === "create" || programModal.mode === "edit") && (
            <div>
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "8px",
                  marginBottom: "2rem",
                  border: "1px solid #bbdefb",
                }}
              >
                <Header
                  as="h5"
                  style={{ margin: "0 0 0.5rem 0", color: "#1976d2" }}
                >
                  <Icon name="info circle" />
                  College Information
                </Header>
                <p style={{ margin: 0, fontSize: "1.1em", fontWeight: "600" }}>
                  {colleges.find((c) => c.id === programModal.collegeId)
                    ?.name || "Unknown College"}
                </p>
              </div>

              <Form>
                <Form.Group widths="equal">
                  <Form.Field required>
                    <label
                      style={{
                        fontSize: "1.1em",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Program Code
                    </label>
                    <Input
                      fluid
                      placeholder="e.g., BSIT, BSCS, MBA"
                      value={programForm.code}
                      onChange={(e, { value }) =>
                        setProgramForm((prev) => ({
                          ...prev,
                          code: value.toUpperCase(),
                        }))
                      }
                      error={programForm.code.trim() === ""}
                    />
                  </Form.Field>
                  <Form.Field required>
                    <label
                      style={{
                        fontSize: "1.1em",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Program Name
                    </label>
                    <Input
                      fluid
                      placeholder="e.g., Bachelor of Science in Information Technology"
                      value={programForm.name}
                      onChange={(e, { value }) =>
                        setProgramForm((prev) => ({ ...prev, name: value }))
                      }
                      error={programForm.name.trim() === ""}
                    />
                  </Form.Field>
                </Form.Group>
              </Form>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions
          style={{ padding: "1.5rem", backgroundColor: "#f8f9fa" }}
        >
          {programModal.mode === "manage" ? (
            <Button onClick={closeProgramModal} size="medium" primary>
              <Icon name="check" />
              Done
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setProgramModal((prev) => ({
                    ...prev,
                    mode: "manage",
                    data: {},
                  }));
                  setProgramForm({ name: "", code: "" });
                }}
                size="medium"
              >
                <Icon name="arrow left" />
                Back to List
              </Button>
              <Button
                primary
                loading={programSaving}
                disabled={!isProgramFormValid()}
                onClick={handleSaveProgram}
                size="medium"
              >
                <Icon name={programModal.mode === "create" ? "plus" : "save"} />
                {programModal.mode === "create"
                  ? "Create Program"
                  : "Update Program"}
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal>

      {/* Enhanced Delete Confirmation Modal */}
      <Modal
        open={deleteConfirm.open}
        onClose={closeDeleteConfirm}
        size="small"
        closeIcon
        dimmer="blurring"
      >
        <Modal.Header
          style={{
            backgroundColor: "#fff2f0",
            borderBottom: "2px solid #ffccc7",
            color: "#cf1322",
            fontSize: "1.3em",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Header
            as="h3"
            style={{
              margin: 0,
              color: "#cf1322",
              display: "flex",
              alignItems: "center",
              fontSize: "1.3em",
            }}
          >
            <Icon
              name="exclamation triangle"
              style={{ marginRight: "0.5rem" }}
            />
            Delete {deleteConfirm.type === "college" ? "College" : "Program"}
          </Header>
        </Modal.Header>

        <Modal.Content style={{ padding: "2rem" }}>
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <Icon
              name="trash alternate"
              size="huge"
              style={{ color: "#ff4d4f", opacity: 0.7, marginBottom: "1rem" }}
            />
            <Header as="h3" style={{ color: "#262626", margin: "0 0 1rem 0" }}>
              Confirm Deletion
            </Header>
            <p
              style={{ fontSize: "1.1em", color: "#595959", lineHeight: "1.6" }}
            >
              Are you sure you want to delete{" "}
              <strong style={{ color: "#cf1322", fontSize: "1.2em" }}>
                "{deleteConfirm.name}"
              </strong>
              ?
            </p>
          </div>

          {deleteConfirm.type === "college" && (
            <Message
              warning
              style={{
                margin: "1.5rem 0",
                border: "1px solid #faad14",
                borderRadius: "8px",
              }}
            >
              <Message.Header
                style={{ color: "#d46b08", marginBottom: "0.5rem" }}
              >
                <Icon name="warning sign" />
                Warning: Cascading Delete
              </Message.Header>
              <Message.List>
                <Message.Item>
                  All associated programs will be permanently deleted
                </Message.Item>
                <Message.Item>Student records may be affected</Message.Item>
                <Message.Item>This action cannot be reversed</Message.Item>
              </Message.List>
            </Message>
          )}

          <Message
            info
            style={{
              margin: "1.5rem 0",
              backgroundColor: "#e6f7ff",
              border: "1px solid #91d5ff",
              borderRadius: "8px",
            }}
          >
            <Message.Header
              style={{ color: "#096dd9", marginBottom: "0.5rem" }}
            >
              <Icon name="info circle" />
              Important Notice
            </Message.Header>
            <p style={{ color: "#0050b3", margin: 0 }}>
              This action is <strong>permanent</strong> and cannot be undone.
              Please make sure you have backups if needed.
            </p>
          </Message>

          <Divider style={{ margin: "2rem 0 1rem 0" }} />

          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "bold",
                color: "#595959",
              }}
            >
              Type "{deleteConfirm.name}" to confirm deletion:
            </label>
            <Input
              fluid
              placeholder={`Enter "${deleteConfirm.name}" to confirm`}
              value={deleteConfirmText}
              onChange={(e, { value }) => setDeleteConfirmText(value)}
              style={{ fontSize: "1.1em" }}
              error={
                deleteConfirmText !== "" &&
                deleteConfirmText !== deleteConfirm.name
              }
            />
            {deleteConfirmText !== "" &&
              deleteConfirmText !== deleteConfirm.name && (
                <div
                  style={{
                    color: "#cf1322",
                    fontSize: "0.9em",
                    marginTop: "0.5rem",
                    fontStyle: "italic",
                  }}
                >
                  Name does not match. Please type exactly: "
                  {deleteConfirm.name}"
                </div>
              )}
          </div>

          <div style={{ textAlign: "center", color: "#8c8c8c" }}>
            <p style={{ margin: 0, fontSize: "0.9em" }}>
              This confirmation helps prevent accidental deletions
            </p>
          </div>
        </Modal.Content>

        <Modal.Actions
          style={{
            padding: "1.5rem 2rem",
            backgroundColor: "#fafafa",
            borderTop: "1px solid #f0f0f0",
            textAlign: "right",
          }}
        >
          <Button
            onClick={closeDeleteConfirm}
            size="large"
            style={{
              marginRight: "1rem",
              minWidth: "120px",
            }}
          >
            <Icon name="cancel" />
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirmDelete}
            size="large"
            disabled={deleteConfirmText !== deleteConfirm.name}
            style={{
              minWidth: "160px",
              fontWeight: "bold",
              opacity: deleteConfirmText !== deleteConfirm.name ? 0.5 : 1,
            }}
          >
            <Icon name="trash alternate outline" />
            Delete Permanently
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

// Export the component
export { CollegeProgramManagement };
export default CollegeProgramManagement;
